import { useEffect, useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import useIntl from 'react-intl/src/components/useIntl';
import { ButtonUI, CustomThemeProvider, Input, Prompt, showNotification, usePortal } from 'sonata-react-components';
import { SessionEntity } from 'sonata-static-storage/lib/entities';
import {
  getSonataSession,
  removeSonataSession,
  setSonataSession
} from 'sonata-static-storage/lib/helpers/session.helpers';

import {
  buildSharedReportRedirectUrl,
  getSessionSharedReportId,
  getSharedReportIdFromPath,
  isSharedReportSession
} from '@/helpers/shareReport/shareReport.helper';
import { createSharedReportSession, getSharedReportSession } from '@/services/sharedReport.service';
import { removeSession } from '@/store/global/session/selectors';

import './sharedReportGate.scss';

import type { ReactElement } from 'react';

type ModalHandler = {
  closeModal: () => void;
  openModal: () => void;
};

function SharedReportAccessModal({ sharedReportId }: Readonly<{ sharedReportId: string }>) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<ModalHandler>(null);
  const targetBody = usePortal('sharedReportPasswordModal');
  const submitButtonId = useId();
  const passwordInputId = useId();
  const [password, setPassword] = useState<string>();

  useEffect(() => {
    modalRef.current?.openModal();
  }, []);

  const submit = async () => {
    try {
      // Send the raw password: the backend hashes (sha512/md5) and compares it to the
      // stored hash. Hashing here too would double-hash and never match.
      const session = await createSharedReportSession(sharedReportId, password);
      if (session?.success === false) {
        showNotification('alert', session.message);
        return;
      }
      setSonataSession(session);
      const report = await getSharedReportSession(sharedReportId, session);
      window.location.assign(buildSharedReportRedirectUrl(report));
    } catch {
      showNotification('alert', 'Given password is incorrect, please try again');
    }
  };

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <Prompt
        className="sharedReportPasswordPrompt"
        closePrompt={() => {
          // todo nothing to do
        }}
        width="714px"
      >
        <Prompt.Icon icon="sharedReportPasswordPrompt__icon fas fa-link" />
        <Prompt.Title className="sharedReportPasswordPrompt__title" variant="default">
          {formatMessage({
            defaultMessage: 'Shared Report Authentication',
            id: 'shareReport.accessModal.title'
          })}
        </Prompt.Title>
        <Prompt.Description className="sharedReportPasswordPrompt__description" variant="default">
          {formatMessage({
            defaultMessage: 'Use the shared report given password in order to continue',
            id: 'shareReport.accessModal.description'
          })}
        </Prompt.Description>
        <form
          className="sharedReportForm"
          onSubmit={e => {
            e.preventDefault();
            submit();
          }}
        >
          <Input
            className="sharedReportForm__passwordInput"
            id={passwordInputId}
            label={formatMessage({
              defaultMessage: 'Password',
              id: 'shareReport.accessModal.passwordLabel'
            })}
            name="sharedReportFormPasswordInput"
            notFloatingPlaceholder
            onChange={async value => {
              if (typeof value === 'string') {
                setPassword(value);
              } else {
                setPassword(undefined);
              }
            }}
            placeholder={formatMessage({
              defaultMessage: 'Write password',
              id: 'shareReport.accessModal.passwordPlaceholder'
            })}
            shape="squareUI"
            type="password"
          />
        </form>
        <Prompt.Buttons position="centered">
          <ButtonUI
            className="sharedReportForm__submitButton"
            disabled={!password}
            id={submitButtonId}
            onClick={() => {
              submit();
            }}
            shape="squareRounded"
            text="Continue"
          />
        </Prompt.Buttons>
      </Prompt>
    </CustomThemeProvider>,
    targetBody
  );
}

/**
 * Mounted between AuthProvider and the router. On the public access route
 * `/sharedReport/:sharedReportId` it always clears any existing sonata session
 * (cookie + store) and shows the password modal, so the viewer starts fresh and
 * must re-authenticate. On every other path it is inert and leaves the session
 * untouched.
 */
function SharedReportGate({ children }: Readonly<{ children: ReactElement }>) {
  const sharedReportId = getSharedReportIdFromPath(window.location.pathname);
  const rawSession = getSonataSession();
  const session = rawSession ? new SessionEntity(rawSession) : null;
  const alreadyAuthorized =
    sharedReportId != null && isSharedReportSession(session) && getSessionSharedReportId(session) === sharedReportId;

  useEffect(() => {
    // Accessing the share link always starts fresh: clear any existing sonata
    // session (cookie + store) so the viewer must authenticate via the modal,
    // unless the session already belongs to this exact share.
    if (sharedReportId && session && !alreadyAuthorized) {
      removeSonataSession();
      removeSession();
    }
  }, [sharedReportId, alreadyAuthorized]);

  if (sharedReportId && !alreadyAuthorized) {
    return <SharedReportAccessModal sharedReportId={sharedReportId} />;
  }
  return children;
}

export default SharedReportGate;
